import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["switch", "morningIndicator", "eveningIndicator"]
  static values = {
    morningSection: String,
    eveningSection: String
  }

  connect() {
    this.scrollHandler = this.handleScroll.bind(this)
    this.observeSections()

    // Set initial state
    this.updateSwitchState()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  observeSections() {
    const morningSection = document.getElementById(this.morningSectionValue)
    const eveningSection = document.getElementById(this.eveningSectionValue)

    if (!morningSection && !eveningSection) return

    // Use Intersection Observer to detect which section is in view
    const options = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const isMorning = entry.target.id === this.morningSectionValue
          this.setActiveSection(isMorning ? 'morning' : 'evening', false)
        }
      })
    }, options)

    if (morningSection) this.observer.observe(morningSection)
    if (eveningSection) this.observer.observe(eveningSection)
  }

  handleScroll() {
    this.updateSwitchState()
  }

  updateSwitchState() {
    const morningSection = document.getElementById(this.morningSectionValue)
    const eveningSection = document.getElementById(this.eveningSectionValue)

    if (!morningSection && !eveningSection) return

    const scrollPosition = window.scrollY + window.innerHeight / 2

    if (morningSection && eveningSection) {
      const morningBottom = morningSection.offsetTop + morningSection.offsetHeight

      if (scrollPosition < morningBottom) {
        this.setActiveSection('morning', false)
      } else {
        this.setActiveSection('evening', false)
      }
    } else if (morningSection) {
      this.setActiveSection('morning', false)
    } else if (eveningSection) {
      this.setActiveSection('evening', false)
    }
  }

  setActiveSection(section, shouldScroll = true) {
    const isMorning = section === 'morning'

    // Update switch visual state
    if (this.hasSwitchTarget) {
      const switchElement = this.switchTarget
      const translate = isMorning ? 'translate-x-0' : 'translate-x-full'
      const removeTranslate = isMorning ? 'translate-x-full' : 'translate-x-0'

      switchElement.classList.remove(removeTranslate)
      switchElement.classList.add(translate)
    }

    // Update text colors
    if (this.hasMorningIndicatorTarget && this.hasEveningIndicatorTarget) {
      if (isMorning) {
        this.morningIndicatorTarget.classList.add('text-white')
        this.morningIndicatorTarget.classList.remove('text-warm-gray-600')
        this.eveningIndicatorTarget.classList.add('text-warm-gray-600')
        this.eveningIndicatorTarget.classList.remove('text-white')
      } else {
        this.eveningIndicatorTarget.classList.add('text-white')
        this.eveningIndicatorTarget.classList.remove('text-warm-gray-600')
        this.morningIndicatorTarget.classList.add('text-warm-gray-600')
        this.morningIndicatorTarget.classList.remove('text-white')
      }
    }

    // Scroll to section if requested
    if (shouldScroll) {
      const targetSection = document.getElementById(
        isMorning ? this.morningSectionValue : this.eveningSectionValue
      )

      if (targetSection) {
        targetSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  }

  switchToMorning(event) {
    event?.preventDefault()
    this.setActiveSection('morning', true)
  }

  switchToEvening(event) {
    event?.preventDefault()
    this.setActiveSection('evening', true)
  }
}
